// AGC: CircularProgress - Minimal circular progress indicator
// AGC: SVG-based arc animation for premium LOD card design
// AGC: DJI Action 5 Pro inspired - clean, minimal, no ticks or needles

import React from 'react'

interface CircularProgressProps {
  progress: number            // AGC: 0-100
  size?: number               // AGC: Diameter in pixels (default 48)
  strokeWidth?: number        // AGC: Ring thickness (default 4)
  showLabel?: boolean         // AGC: Show percentage in center (default true)
  color?: string              // AGC: Progress ring color (default white)
  backgroundColor?: string    // AGC: Background ring color (default rgba(255,255,255,0.2))
  className?: string          // AGC: Additional CSS classes
}

/**
 * AGC: CircularProgress - Clean circular progress ring
 * AGC: Designed for overlay on dark backgrounds (LOD card images)
 * AGC: Uses SVG circle with stroke-dasharray for smooth animation
 */
export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  size = 48,
  strokeWidth = 4,
  showLabel = true,
  color = '#ffffff',
  backgroundColor = 'rgba(255, 255, 255, 0.2)',
  className = '',
}) => {
  // AGC: Clamp progress to 0-100 range
  const clampedProgress = Math.max(0, Math.min(100, progress))
  
  // AGC: Calculate SVG dimensions
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  
  // AGC: Calculate stroke-dashoffset for progress arc
  // AGC: Start from top (rotate -90deg) and go clockwise
  const dashOffset = circumference - (clampedProgress / 100) * circumference
  
  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* AGC: Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
        />
        
        {/* AGC: Progress ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      
      {/* AGC: Percentage label */}
      {showLabel && (
        <div 
          className="absolute inset-0 flex items-center justify-center text-white font-semibold"
          style={{ fontSize: size * 0.25 }}
        >
          {Math.round(clampedProgress)}%
        </div>
      )}
    </div>
  )
}

export default CircularProgress
